import axios from "axios";
import { InfoCircle } from "iconsax-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
// import { Link } from "react-router-dom";
import Select from "react-select";
import { commonConfigs, urls } from "../../../../../config";
import beveatApiStaging from "../../../../api/staging_beveat_api.js";
import beveatApiProduction from "../../../../api/production_beveat_api.js";

const errorIcon = (
  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
    <InfoCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
  </div>
);

// Accept props from the Layout component
// setOpenModal: function to close this modal
// refreshData: function to reload the table list after a successful creation
export default function CreateRoleAccessModal({ setOpenModal, refreshData }) {
  const [isDisabled, setIsDisabled] = useState(false);
  const [accessOptions, setAccessOptions] = useState([]);
  const [loadData, setLoadData] = useState(true);

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm({});

  let getAllAccessPagesUrl = ``;
  let createAccessRolesUrl = ``;
  let apiHeaders;
  let apiInstance;

  // Configuration Logic
  if (process.env.REACT_APP_ENV === "staging") {
    createAccessRolesUrl =
      urls.baseUrl +
      commonConfigs.apiUrls.createAccessRole();
    getAllAccessPagesUrl =
      urls.baseUrl +
      commonConfigs.apiUrls.getAllAccessPages();
    apiHeaders = commonConfigs.apiHeader;
    apiInstance = beveatApiStaging;
  } else {
    createAccessRolesUrl =
      urls.prodUrl +
      commonConfigs.apiUrls.createAccessRole();
    getAllAccessPagesUrl =
      urls.prodUrl +
      commonConfigs.apiUrls.getAllAccessPages();
    apiHeaders = commonConfigs.prodApiHeader;
    apiInstance = beveatApiProduction;
  }

  // MODIFIED: Formatting data without mutating the original 'data' object
  function formatData(data) {
    const rightsArray = data.access_rights.map(item => item.value);

    return {
      ...data,
      access_rights: rightsArray,
      updated_by: null,
    };
  }

  const onSubmit = (data) => {
    setIsDisabled(true); // Disable the submit button

    const payload = formatData(data);
    // console.log(payload);
    apiInstance
      .post(createAccessRolesUrl, payload, { headers: apiHeaders })
      .then((res) => {
        try {
          if (res.data.status !== "success") {
            alert("Error creating role");
          } else {
            alert("Role Created Successfully");
            // Instead of redirecting with window.location, we refresh the table and close modal
            if (refreshData) refreshData();
            setOpenModal(false);
          }
        } catch (err) {
          console.log(err);
        } finally {
          setIsDisabled(false); // Enable the submit button after API call
        }
      })
      .catch((err) => {
        alert(`Error: ${err}`);
        setIsDisabled(false); // Enable the submit button if there's an error
      });
  };

  useEffect(() => {
    async function getAccessPages() {
      await apiInstance
        .get(getAllAccessPagesUrl, { headers: apiHeaders })
        .then((res) => {
          var finalAccess = [];
          var response = res.data.result.data;
          // console.log(response);

          for (var i = 0; i < response.length; i++) {
            if (response[i].access_name_option !== null) {
              finalAccess.push({
                value: response[i].access_id,
                label: `${response[i].access_name} [${response[i].access_name_option}]`,
              });
            } else {
              finalAccess.push({
                value: response[i].access_id,
                label: response[i].access_name,
              });
            }
          }
          setAccessOptions(finalAccess);
          setLoadData(false);
        })
        .catch((err) => {
          console.log(err);
        });
    }
    if (loadData) {
      getAccessPages().catch((err) => {
        console.log("err for access types: " + err);
      });
    }
  }, []);

  // Render the form inside a Modal UI instead of a full page
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
      {/* Backdrop/Overlay */}
      <div
        className="fixed inset-0 bg-black opacity-40"
        onClick={() => setOpenModal(false)}
      ></div>

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl mx-auto my-6 z-50">
        <div className="border-0 rounded-2xl shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none p-8">

          <div className="flex items-start justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Create Role Access</h3>
            <button
              className="p-1 ml-auto bg-transparent border-0 text-gray-400 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
              onClick={() => setOpenModal(false)}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Role Name Input */}
            <div className="mb-6">
              <label htmlFor="role_name" className="block text-sm font-medium text-gray-600 mb-2 text-left">
                Role Name
              </label>
              <div className="relative rounded-md shadow-sm text-black">
                <input
                  type="text"
                  name="role_name"
                  id="role_name"
                  className={
                    !errors.role_name
                      ? "block w-full pr-10 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white text-black"
                      : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                  }
                  placeholder="Enter Name of Role"
                  {...register("role_name", { required: true })}
                />
                {errors.role_name?.type === "required" && errorIcon}
              </div>
            </div>

            {/* Access Types Select */}
            <div className="mb-8">
              <label htmlFor="access_rights" className="block text-sm font-medium text-gray-600 mb-2 text-left">
                Access Types
              </label>
              <div className="text-black">
                <Controller
                  name="access_rights"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      options={accessOptions}
                      isSearchable={true}
                      isMulti={true}
                      placeholder="Select at least one option"
                      className="text-sm"
                    />
                  )}
                />
                {errors.access_rights && <p className="text-red-500 text-xs mt-1 text-left">This field is required</p>}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                className="hover:bg-red-500 text-red-500 hover:text-white border border-red-500 font-bold py-2 px-10 rounded-full transition-colors"
                onClick={() => setOpenModal(false)}
                disabled={isDisabled}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="hover:bg-blue-500 text-blue-500 hover:text-white border border-blue-500 font-bold py-2 px-10 rounded-full transition-colors"
                disabled={isDisabled}
              >
                {isDisabled ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}